/**
 * SchoolLookupInput — ARIA APG 1.2 combobox typeahead for school search.
 *
 * Implements WI-23b: search by school name, mascot, or abbreviation.
 *
 * Accessibility:
 *  - role="combobox" on the input with aria-expanded, aria-controls, aria-activedescendant.
 *  - role="listbox" on the dropdown ul.
 *  - role="option" + unique id on each result li.
 *  - Virtual focus: DOM focus stays on input; focusedIndex drives aria-activedescendant.
 *  - Keyboard: ArrowDown/Up, Enter, Escape.
 *
 * Debounce: 250 ms after last keystroke. AbortController cancels stale requests.
 * Timeout: 6 s AbortController timeout per TC-23b-030.
 * WI-29: navigation always produces bare /schools/<slug> — no context carry-forward.
 */

import React, {
  useId,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSchoolLookup, SchoolLookupResult } from '../api/lookup';

// ── Division display labels ────────────────────────────────────────────────────
const DIVISION_LABEL: Record<string, string> = {
  ncaa_d1: 'D1',
  ncaa_d2: 'D2',
  ncaa_d3: 'D3',
  naia: 'NAIA',
  njcaa: 'NJCAA',
};

// ── Types ─────────────────────────────────────────────────────────────────────
type DropdownStatus = 'idle' | 'loading' | 'results' | 'empty' | 'error';

const DEBOUNCE_MS = 250;
const TIMEOUT_MS = 6000;
const MIN_QUERY_LEN = 2;

// ── Component ─────────────────────────────────────────────────────────────────

interface SchoolLookupInputProps {
  /** When true the input is in mobile-expanded mode (full-width, auto-focused). */
  mobileExpanded?: boolean;
  /** CSS class overrides for the input element. */
  inputClassName?: string;
}

export function SchoolLookupInput({
  mobileExpanded = false,
  inputClassName = '',
}: SchoolLookupInputProps) {
  const navigate = useNavigate();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionIdPrefix = `${baseId}-option-`;

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SchoolLookupResult[]>([]);
  const [status, setStatus] = useState<DropdownStatus>('idle');
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Auto-focus when expanded on mobile.
  useEffect(() => {
    if (mobileExpanded) {
      inputRef.current?.focus();
    }
  }, [mobileExpanded]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setStatus('idle');
        setFocusedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // ── Navigation — WI-29: always navigate to bare /schools/<slug> ──────────────
  // No context is carried from the current page. The SchoolLandingPicker (rendered
  // when sport + target_year are absent from the destination URL) handles sport/year
  // selection explicitly. Resolves OQ-29-2: no context carry-forward.
  function buildNavUrl(slug: string): string {
    return `/schools/${slug}`;
  }

  function selectResult(result: SchoolLookupResult) {
    const url = buildNavUrl(result.slug);
    setQuery('');
    setStatus('idle');
    setFocusedIndex(-1);
    navigate(url);
  }

  // ── Debounced search ─────────────────────────────────────────────────────────
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (q: string) => {
    // Cancel any in-flight request.
    abortRef.current?.abort();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const controller = new AbortController();
    abortRef.current = controller;

    // 6 s timeout per TC-23b-030.
    timeoutRef.current = setTimeout(() => {
      controller.abort();
    }, TIMEOUT_MS);

    setStatus('loading');
    setFocusedIndex(-1);

    try {
      const data = await fetchSchoolLookup(q, 8, controller.signal);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setResults(data);
      setStatus(data.length === 0 ? 'empty' : 'results');
    } catch (err) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if ((err as Error).name === 'AbortError') {
        // Request was cancelled — do not update state.
        return;
      }
      setStatus('error');
    }
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setQuery(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    if (val.length < MIN_QUERY_LEN) {
      setStatus('idle');
      setResults([]);
      setFocusedIndex(-1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      doSearch(val);
    }, DEBOUNCE_MS);
  }

  // ── Keyboard navigation ───────────────────────────────────────────────────────
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const isOpen = status === 'results' || status === 'loading' || status === 'empty' || status === 'error';

    if (e.key === 'Escape') {
      e.preventDefault();
      setStatus('idle');
      setFocusedIndex(-1);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (status !== 'results') return;
      setFocusedIndex((prev) => {
        if (prev >= results.length - 1) return -1; // wrap back to input
        return prev + 1;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (status !== 'results') return;
      setFocusedIndex((prev) => {
        if (prev <= 0) return -1; // back to input
        return prev - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      if (focusedIndex >= 0 && focusedIndex < results.length) {
        e.preventDefault();
        selectResult(results[focusedIndex]);
      }
      return;
    }

    // Any other key: reset focused index.
    if (isOpen) {
      setFocusedIndex(-1);
    }
  }

  // ── Render helpers ────────────────────────────────────────────────────────────
  const isOpen =
    status === 'loading' ||
    status === 'results' ||
    status === 'empty' ||
    status === 'error';

  const activedescendant =
    focusedIndex >= 0 ? `${optionIdPrefix}${focusedIndex}` : undefined;

  // Count announcement for screen readers.
  const srCount =
    status === 'results'
      ? `${results.length} school${results.length !== 1 ? 's' : ''} found.`
      : status === 'empty'
      ? 'No schools found.'
      : '';

  const inputBase =
    'text-sm px-3 py-1.5 border border-gray-300 rounded-lg bg-white placeholder-gray-400 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1';

  const widthClass = mobileExpanded ? 'flex-1 min-w-0' : 'w-56 lg:w-72';

  return (
    <div ref={wrapperRef} className={`relative ${widthClass}`}>
      {/* SR-only live region for result count announcements */}
      <span className="sr-only" aria-live="polite">
        {srCount}
      </span>

      {/* Combobox input */}
      <div className="relative">
        {/* Magnifier icon inside left of input */}
        <span
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          aria-hidden="true"
        >
          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activedescendant}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Search schools…"
          className={`${inputBase} ${widthClass} pl-8 ${inputClassName}`}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="School search results"
          className={`
            absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg
            shadow-lg z-40 overflow-y-auto
            ${mobileExpanded ? 'max-h-[50vh]' : 'max-h-[320px]'}
          `}
        >
          {/* Loading state */}
          {status === 'loading' && (
            <li
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500"
            >
              <span
                className="inline-block w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              />
              Searching…
            </li>
          )}

          {/* No results */}
          {status === 'empty' && (
            <li
              role="status"
              aria-live="polite"
              className="px-3 py-2 text-sm text-gray-500"
            >
              No schools found.
            </li>
          )}

          {/* Error state */}
          {status === 'error' && (
            <li role="alert" className="px-3 py-2 text-sm text-gray-500">
              Search unavailable
            </li>
          )}

          {/* Results */}
          {status === 'results' &&
            results.map((result, index) => {
              const isFocused = index === focusedIndex;
              const optionId = `${optionIdPrefix}${index}`;
              const divisionLabel = DIVISION_LABEL[result.division] ?? result.division;
              return (
                <li
                  key={result.slug}
                  id={optionId}
                  role="option"
                  aria-selected={isFocused}
                  aria-label={`${result.name}, ${divisionLabel}, ${result.region}`}
                  className={`
                    relative flex items-start justify-between px-3 py-2 cursor-pointer
                    border-b border-gray-100 last:border-b-0
                    ${isFocused ? 'bg-[#E8F5EE]' : 'hover:bg-[#E8F5EE]'}
                    group
                  `}
                  onMouseDown={(e) => {
                    // mousedown fires before blur; prevent blur so we can navigate.
                    e.preventDefault();
                    selectResult(result);
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <div className="flex flex-col min-w-0 flex-1">
                    <span
                      className={`text-sm font-medium truncate ${
                        isFocused ? 'text-brand-primary' : 'text-gray-900'
                      }`}
                    >
                      {result.name}
                    </span>
                    <span className="text-xs text-gray-600 mt-0.5">
                      {divisionLabel} &middot; {result.region}
                    </span>
                    {result.matched_alias && (
                      <span className="text-[11px] text-gray-500 italic mt-0.5">
                        matched &ldquo;{result.matched_alias.alias}&rdquo;
                      </span>
                    )}
                  </div>
                  {/* Chevron — visible on focused/hovered row */}
                  {isFocused && (
                    <span
                      className="ml-2 mt-0.5 text-brand-primary flex-shrink-0"
                      aria-hidden="true"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
